// src/Components/Goals.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Flag as GoalIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  RadioButtonUnchecked as NotStartedIcon,
  Schedule as InProgressIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { format, parseISO } from 'date-fns';
import EmojiPicker from './EmojiPicker';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import { notifyTaggedMembers } from '../services/notificationService';
import logger from '../utils/logger';

const Goals = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const defaultFormData = {
    title: '',
    description: '',
    category: 'family',
    status: 'not_started',
    progress: 0,
    targetDate: '',
    startDate: '',
    priority: 'medium',
    assignedTo: '',
    budget: '',
    milestones: [],
    notes: '',
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  };
  const [formData, setFormData] = useState(defaultFormData);

  // Milestone management state
  const [milestoneInput, setMilestoneInput] = useState({ title: '', targetDate: '', completed: false });
  const db = getFirestore();

  const categories = [
    { value: 'family', label: 'Family Goal', color: '#2e7d32' },
    { value: 'financial', label: 'Financial', color: '#689f38' },
    { value: 'health', label: 'Health & Wellness', color: '#0288d1' },
    { value: 'education', label: 'Education', color: '#7b1fa2' },
    { value: 'travel', label: 'Travel & Adventure', color: '#f57c00' },
    { value: 'home', label: 'Home Improvement', color: '#6d4c41' }
  ];

  const statuses = [
    { value: 'not_started', label: 'Not Started', icon: <NotStartedIcon />, color: '#9e9e9e' },
    { value: 'in_progress', label: 'In Progress', icon: <InProgressIcon />, color: '#2196f3' },
    { value: 'completed', label: 'Completed', icon: <CompleteIcon />, color: '#4caf50' }
  ];

  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'goals'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const goalsData = [];
        querySnapshot.forEach((doc) => {
          goalsData.push({ id: doc.id, ...doc.data() });
        });
        setGoals(goalsData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching goals:', error);
        setError('Failed to load goals');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Goal title is required');
      return;
    }

    // Validate private goals have at least one member or group selected
    if (formData.privacy?.visibility === 'private' &&
        (!formData.privacy.memberIds?.length && !formData.privacy.groupTypes?.length)) {
      setError('Please select at least one member or group for private goals');
      return;
    }

    try {
      const goalData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        status: formData.status,
        progress: formData.progress,
        targetDate: formData.targetDate,
        startDate: formData.startDate,
        priority: formData.priority,
        assignedTo: formData.assignedTo,
        budget: formData.budget,
        milestones: formData.milestones || [],
        notes: formData.notes,
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      };

      let goalId = editingGoal?.id;
      if (editingGoal) {
        await updateDoc(doc(db, 'families', familyBoardId, 'goals', editingGoal.id), {
          ...goalData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Goal updated');

        // Notify newly tagged members on edit
        const previousTaggedMembers = editingGoal.taggedMembers || [];
        const newTaggedMembers = (formData.taggedMembers || []).filter(
          memberId => !previousTaggedMembers.includes(memberId)
        );
        if (newTaggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, newTaggedMembers, {
            type: 'goal',
            id: editingGoal.id,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
            actionUrl: '/goals'
          });
        }
      } else {
        const docRef = await addDoc(collection(db, 'families', familyBoardId, 'goals'), goalData);
        goalId = docRef.id;
        setSuccess('Goal created');

        // Notify tagged members for new goals
        if (formData.taggedMembers && formData.taggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, formData.taggedMembers, {
            type: 'goal',
            id: goalId,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
            actionUrl: '/goals'
          });
        }
      }

      setDialogOpen(false);
      setEditingGoal(null);
      setFormData(defaultFormData);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving goal:', error);
      setError('Failed to save goal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'goals', id));
      setSuccess('Goal deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting goal:', error);
      setError('Failed to delete goal');
    }
  };

  const handleProgressUpdate = async (goalId, newProgress) => {
    try {
      const newStatus = newProgress === 100 ? 'completed' : newProgress > 0 ? 'in_progress' : 'not_started';
      await updateDoc(doc(db, 'families', familyBoardId, 'goals', goalId), {
        progress: newProgress,
        status: newStatus,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      logger.error('Error updating progress:', error);
      setError('Failed to update progress');
    }
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      description: goal.description || '',
      category: goal.category,
      status: goal.status,
      progress: goal.progress || 0,
      targetDate: goal.targetDate || '',
      startDate: goal.startDate || '',
      priority: goal.priority || 'medium',
      assignedTo: goal.assignedTo || '',
      budget: goal.budget || '',
      milestones: goal.milestones || [],
      notes: goal.notes || '',
      privacy: goal.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: goal.taggedMembers || []
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingGoal(null);
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  const getCategoryDetails = (categoryValue) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  const getStatusDetails = (statusValue) => {
    return statuses.find(stat => stat.value === statusValue) || statuses[0];
  };

  // Milestone management functions
  const addMilestone = () => {
    if (!milestoneInput.title.trim()) return;
    setFormData({
      ...formData,
      milestones: [...(formData.milestones || []), { ...milestoneInput, id: Date.now() }]
    });
    setMilestoneInput({ title: '', targetDate: '', completed: false });
  };

  const removeMilestone = (id) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter(m => m.id !== id)
    });
  };

  const toggleMilestone = async (goalId, milestoneId) => {
    try {
      const goal = goals.find(g => g.id === goalId);
      const updatedMilestones = goal.milestones.map(m =>
        m.id === milestoneId ? { ...m, completed: !m.completed } : m
      );

      // Calculate progress based on completed milestones
      const completedCount = updatedMilestones.filter(m => m.completed).length;
      const newProgress = updatedMilestones.length > 0
        ? Math.round((completedCount / updatedMilestones.length) * 100)
        : goal.progress;

      await updateDoc(doc(db, 'families', familyBoardId, 'goals', goalId), {
        milestones: updatedMilestones,
        progress: newProgress,
        status: newProgress === 100 ? 'completed' : newProgress > 0 ? 'in_progress' : 'not_started',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      logger.error('Error toggling milestone:', error);
      setError('Failed to update milestone');
    }
  };

  // Privacy filter — same pattern as Events
  const canViewGoal = (goal) => {
    if (goal.authorId === currentUser?.uid) return true;
    if (!goal.privacy || goal.privacy.visibility === 'public') return true;
    if (goal.privacy.visibility === 'private') {
      if (goal.privacy.memberIds?.includes(currentUser?.uid)) return true;
      if (goal.privacy.groupTypes?.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        if (goal.privacy.groupTypes.some(g => userGroups.includes(g))) return true;
      }
      return false;
    }
    return true;
  };

  const visibleGoals = goals.filter(goal => canViewGoal(goal));

  // Group by status
  const activeGoals = visibleGoals.filter(g => g.status !== 'completed');
  const completedGoals = visibleGoals.filter(g => g.status === 'completed');

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Goals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and achieve your family's dreams together
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          New Goal
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {visibleGoals.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <TrendingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No family goals yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set meaningful goals and track your family's progress together!
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Create First Goal
          </Button>
        </Paper>
      ) : (
        <Box>
          {/* Active Goals */}
          {activeGoals.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Active Goals
              </Typography>
              <Grid container spacing={3}>
                {activeGoals.map((goal) => {
                  const categoryDetails = getCategoryDetails(goal.category);
                  const statusDetails = getStatusDetails(goal.status);

                  return (
                    <Grid item xs={12} md={6} key={goal.id}>
                      <Card sx={{
                        height: '100%',
                        '&:hover': { boxShadow: 6 },
                        transition: 'all 0.3s',
                        border: '2px solid',
                        borderColor: categoryDetails.color
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={categoryDetails.label}
                                size="small"
                                sx={{
                                  bgcolor: categoryDetails.color,
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                              <Chip
                                icon={statusDetails.icon}
                                label={statusDetails.label}
                                size="small"
                                sx={{
                                  bgcolor: statusDetails.color,
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                              {goal.priority && goal.priority !== 'medium' && (
                                <Chip
                                  label={goal.priority.toUpperCase()}
                                  size="small"
                                  color={
                                    goal.priority === 'urgent' ? 'error' :
                                    goal.priority === 'high' ? 'warning' :
                                    goal.priority === 'low' ? 'default' : 'primary'
                                  }
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              {goal.privacy?.visibility === 'private' && (
                                <Chip
                                  icon={<LockIcon />}
                                  label="Private"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </Box>
                            {goal.authorId === currentUser.uid && (
                              <Box>
                                <IconButton size="small" onClick={() => openEditDialog(goal)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDelete(goal.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>

                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: categoryDetails.color }}>
                            {goal.title}
                          </Typography>

                          {goal.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {goal.description}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                            {goal.assignedTo && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Assigned to: {goal.assignedTo}
                                </Typography>
                              </Box>
                            )}
                            {goal.budget && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  💰 Budget: {goal.budget}
                                </Typography>
                              </Box>
                            )}
                            {goal.startDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  📅 Started: {format(parseISO(goal.startDate), 'MMM d, yyyy')}
                                </Typography>
                              </Box>
                            )}
                            {goal.targetDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GoalIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Target: {format(parseISO(goal.targetDate), 'MMM d, yyyy')}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Milestones Section */}
                          {goal.milestones && goal.milestones.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                Milestones ({goal.milestones.filter(m => m.completed).length}/{goal.milestones.length})
                              </Typography>
                              <List dense sx={{ py: 0 }}>
                                {goal.milestones.slice(0, 3).map((milestone) => (
                                  <ListItem
                                    key={milestone.id}
                                    sx={{ px: 0, py: 0.5 }}
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => toggleMilestone(goal.id, milestone.id)}
                                      >
                                        {milestone.completed ? <CompleteIcon color="success" fontSize="small" /> : <NotStartedIcon fontSize="small" />}
                                      </IconButton>
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            textDecoration: milestone.completed ? 'line-through' : 'none',
                                            color: milestone.completed ? 'text.secondary' : 'text.primary'
                                          }}
                                        >
                                          {milestone.title}
                                        </Typography>
                                      }
                                      secondary={
                                        milestone.targetDate && (
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                            {format(parseISO(milestone.targetDate), 'MMM d')}
                                          </Typography>
                                        )
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                              {goal.milestones.length > 3 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  + {goal.milestones.length - 3} more milestones
                                </Typography>
                              )}
                            </Box>
                          )}

                          {goal.notes && (
                            <Paper sx={{ p: 1, bgcolor: 'info.lighter', mb: 2 }}>
                              <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                📝 {goal.notes}
                              </Typography>
                            </Paper>
                          )}

                          <Divider sx={{ my: 2 }} />

                          {/* Progress Section */}
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Progress
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: categoryDetails.color }}>
                                {goal.progress || 0}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={goal.progress || 0}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'background.default',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: categoryDetails.color,
                                  borderRadius: 4
                                }
                              }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              {[0, 25, 50, 75, 100].map((value) => (
                                <Button
                                  key={value}
                                  size="small"
                                  variant={goal.progress === value ? 'contained' : 'outlined'}
                                  onClick={() => handleProgressUpdate(goal.id, value)}
                                  sx={{ flex: 1, fontSize: '0.7rem' }}
                                >
                                  {value}%
                                </Button>
                              ))}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Created by {goal.authorName}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Completed Goals */}
          {completedGoals.length > 0 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon /> Completed Goals
              </Typography>
              <Grid container spacing={2}>
                {completedGoals.map((goal) => {
                  const categoryDetails = getCategoryDetails(goal.category);

                  return (
                    <Grid item xs={12} md={4} key={goal.id}>
                      <Card sx={{ bgcolor: 'success.light', opacity: 0.8, '&:hover': { opacity: 1 }, transition: 'opacity 0.3s' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Chip
                              icon={<CompleteIcon />}
                              label={categoryDetails.label}
                              size="small"
                              sx={{
                                bgcolor: 'success.main',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                            {goal.authorId === currentUser.uid && (
                              <IconButton size="small" onClick={() => handleDelete(goal.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                            {goal.title}
                          </Typography>
                          {goal.description && (
                            <Typography variant="caption" color="text.secondary">
                              {goal.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* Create/Edit Goal Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGoal ? 'Edit Goal' : 'Create New Goal'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Goal Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., Save for family vacation, Learn a new skill"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Details about this goal..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, description: formData.description + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {statuses.map((stat) => (
                  <MenuItem key={stat.value} value={stat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {stat.icon}
                      <Typography>{stat.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Date (Optional)"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Target Date (Optional)"
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Priority Level</InputLabel>
              <Select
                value={formData.priority}
                label="Priority Level"
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <MenuItem value="low">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Low" size="small" color="default" sx={{ minWidth: 60 }} />
                    <Typography variant="body2">- Can wait</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="medium">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Medium" size="small" color="primary" sx={{ minWidth: 60 }} />
                    <Typography variant="body2">- Normal priority</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="high">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="High" size="small" color="warning" sx={{ minWidth: 60 }} />
                    <Typography variant="body2">- Important</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="urgent">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Urgent" size="small" color="error" sx={{ minWidth: 60 }} />
                    <Typography variant="body2">- Critical priority</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Assigned To (Optional)"
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  placeholder="e.g., Dad, Mom"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Budget (Optional)"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="$1,000"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }}>
              <Chip label="Milestones" size="small" />
            </Divider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Milestone"
                  value={milestoneInput.title}
                  onChange={(e) => setMilestoneInput({ ...milestoneInput, title: e.target.value })}
                  placeholder="Complete step 1"
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  label="Date"
                  type="date"
                  value={milestoneInput.targetDate}
                  onChange={(e) => setMilestoneInput({ ...milestoneInput, targetDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <Button onClick={addMilestone} variant="contained" size="small">
                  <AddIcon />
                </Button>
              </Box>

              {formData.milestones && formData.milestones.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <List dense>
                    {formData.milestones.map((milestone) => (
                      <ListItem
                        key={milestone.id}
                        secondaryAction={
                          <IconButton edge="end" size="small" onClick={() => removeMilestone(milestone.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={milestone.title}
                          secondary={milestone.targetDate && `Due: ${format(parseISO(milestone.targetDate), 'MMM d, yyyy')}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>

            <TextField
              fullWidth
              label="Notes (Optional)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
              placeholder="Additional notes or updates about this goal..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setFormData({ ...formData, notes: formData.notes + emoji })}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />

            <Divider sx={{ my: 1 }}>
              <Chip label="Privacy & Sharing" size="small" />
            </Divider>

            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this goal?"
            />

            <Box sx={{ mt: 2 }}>
              <TagMembers
                familyCode={familyBoardId}
                value={formData.taggedMembers}
                onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
                label="Tag Family Members"
                helperText="Tagged members will receive a notification about this goal"
              />
            </Box>

            <Box>
              <Typography variant="caption" gutterBottom>
                Initial Progress: {formData.progress}%
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[0, 25, 50, 75, 100].map((value) => (
                  <Button
                    key={value}
                    size="small"
                    variant={formData.progress === value ? 'contained' : 'outlined'}
                    onClick={() => setFormData({ ...formData, progress: value })}
                    sx={{ flex: 1 }}
                  >
                    {value}%
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingGoal ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Goals;
